import React, {useMemo, useState} from 'react';
import {Box, Typography} from '@mui/material';
import './AnswerPanel.scss';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import {QALabel} from '../QALabel';
import ViewDocsFlyout from '../../view-docs/view-docs-flyout/ViewDocsFlyout';
import {t} from 'i18next';
import ReactMarkdown, {defaultUrlTransform} from 'react-markdown';
import rehypeSanitize, {defaultSchema} from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import type {Components} from 'react-markdown';
type LinkRendererProps = React.ComponentPropsWithoutRef<'a'> & {node?: unknown};
type CodeRendererProps = React.ComponentPropsWithoutRef<'code'> & {
  inline?: boolean;
  node?: unknown;
};

interface AnswerProps {
  answer: string;
  conversationNumber: number;
  conversationId: string;
  transcriptId?: number | string;
  indexId: number;
  onViewDocs?: (conversationId: string) => void;
  onLinkClick?: (docId: string, documentId: number) => void;
  isStreaming: boolean;
}

const sanitizeSchema = {
  ...defaultSchema,
  protocols: {
    ...defaultSchema.protocols,
    href: [...(defaultSchema.protocols?.href || []), 'documentid'],
  },
};

const markdownUrlTransform = (value: string) => {
  if (/^documentid:\d+$/i.test(value)) {
    return value;
  }

  return defaultUrlTransform(value);
};

const AnswerPanel: React.FC<AnswerProps> = ({
  answer,
  conversationNumber,
  conversationId,
  transcriptId = 0,
  indexId,
  onViewDocs,
  onLinkClick,
  isStreaming,
}) => {
  const [isViewDocsFlyoutOpen, setIsViewDocsFlyoutOpen] = useState(false);
  const markdownComponents = useMemo<Components>(
    () => ({
      a: ({href, children}: LinkRendererProps) => {
        const linkText = children?.toString().trim() ?? '';
        const target = href?.trim() ?? '';
        const newDocumentMatch = target.match(/^documentid:(\d+)$/i);
        const oldDocumentMatch = target.match(/^\d+$/);

        const isDocumentLink =
          newDocumentMatch ||
          (oldDocumentMatch && /^[A-Z]+\d+[A-Z]*$/i.test(linkText));

        const documentId = newDocumentMatch
          ? Number(newDocumentMatch[1])
          : oldDocumentMatch
            ? Number(target)
            : null;

        if (isDocumentLink && documentId !== null) {
          return (
            <span
              className="answer-link"
              onClick={(e) => {
                e.preventDefault();
                onLinkClick?.(linkText, documentId);
              }}
              data-testid={`${linkText}-markdown-component`}
            >
              {linkText}
            </span>
          );
        }

        if (isDocumentLink) {
          return (
            <span
              className="answer-link"
              onClick={(e) => {
                e.preventDefault();
                onLinkClick?.(linkText, Number(documentId));
              }}
              data-testid={`${linkText}-markdown-component`}
            >
              {linkText}
            </span>
          );
        }

        // Normal links like email, website, etc.
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="answer-link"
            data-testid={`${linkText}-external-link`}
          >
            {children}
          </a>
        );
      },
      code: ({
        inline,
        className,
        children,
        node,
        ...props
      }: CodeRendererProps) => {
        if (inline) {
          const index = (node as any)?.position?.start?.offset || 0;
          return (
            <code
              className="inline-code"
              {...props}
              data-testid="answer-panel-code-${docId}"
            >
              {children}
            </code>
          );
        }

        return (
          <pre className="code-block" data-testid="answer-panel-pre">
            <code
              className={className}
              {...props}
              data-testid="answer-panel-code-btn"
            >
              {children}
            </code>
          </pre>
        );
      },
    }),
    [onLinkClick]
  );

  const renderedContent = useMemo(() => {
    if (!answer) return null;

    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
        components={markdownComponents}
        urlTransform={markdownUrlTransform}
      >
        {answer + (isStreaming ? '|' : '')}
      </ReactMarkdown>
    );
  }, [answer, markdownComponents]);

  const handleViewDocs = () => {
    setIsViewDocsFlyoutOpen(true);
    onViewDocs?.(conversationId);
  };

  return (
    <Box className="answer-container" data-testid="answer-panel">
      {isViewDocsFlyoutOpen && (
        <ViewDocsFlyout
          isOpen={isViewDocsFlyoutOpen}
          onClose={() => {
            setIsViewDocsFlyoutOpen(false);
          }}
          conversationNumber={conversationNumber}
          transcriptId={transcriptId}
          indexId={indexId}
          conversationId={conversationId}
        />
      )}
      <Box className="answer-header">
        <QALabel label={`A${conversationNumber}`} />
      </Box>
      <Box className="answer-content">
        <Typography
          component="div"
          className="answer-text answer-markdown "
          data-testid={`single-answer-text-${conversationNumber}`}
        >
          {renderedContent}
        </Typography>
      </Box>
      <Box className="answer-footer">
        {!isStreaming && (
          <ArrButton
            variant="primary"
            size="small"
            className="view-docs-btn"
            onClick={handleViewDocs}
            dataTestId="answer-panel-view-docs-button"
          >
            {t('ai.investigate.questionAndAnswerPanel.answerPanel.viewDocs')}
          </ArrButton>
        )}
      </Box>
    </Box>
  );
};

export default AnswerPanel;
